/**
 * Seating math: translates a guest count + head table configuration into
 * the number of guest tables required, per PRD §7.2.
 *
 * This module is pure — no I/O, no React, no randomness. Same input always
 * produces the same output, which is what lets it be unit-tested directly
 * against the PRD's worked examples.
 */

import type { HeadTableType, LayoutStyle } from '../types';

export interface SeatingPlan {
  /** Guests seated at round/rectangle guest tables (excludes head/sweetheart table occupants). */
  seatableGuests: number;
  /** Number of round tables needed (relevant for classic_banquet, mixed_modern). */
  roundTableCount: number;
  /** Number of 8' rectangle tables needed (relevant for long_table_feast). */
  rectTable8Count: number;
  /** Number of cocktail tables needed (relevant for lounge_cocktail only). */
  cocktailTableCount: number;
  /** Number of guests planned to be seated at round tables in lounge_cocktail style. */
  loungeSeatedGuests: number;
  /** Number of guests planned to mingle at cocktail tables in lounge_cocktail style. */
  loungeStandingGuests: number;
}

const SEATS_PER_ROUND_TABLE = 8;
const SEATS_PER_RECT_8_TABLE = 8;
const STANDING_GUESTS_PER_COCKTAIL_TABLE = 4;
const LOUNGE_SEATED_RATIO = 0.7;

/**
 * Core seatable-guest calculation: total guest count minus whoever sits at
 * the head table or sweetheart table (PRD §7.2).
 */
export function calculateSeatableGuests(
  guestCount: number,
  headTableType: HeadTableType,
  headTablePartySize: number | null
): number {
  if (headTableType === 'sweetheart_table') {
    // The couple is seated at the sweetheart table, not counted in guest tables.
    return Math.max(0, guestCount - 2);
  }
  const partySize = headTablePartySize ?? 6;
  return Math.max(0, guestCount - partySize);
}

/**
 * Round table count for guest seating.
 *
 * Note on the PRD's "avoid one overcrowded leftover table" guidance: simple
 * ceil() division already satisfies this — it never packs a remainder onto
 * an already-full table, it always opens a new (possibly sparsely filled)
 * table to hold any remainder. An earlier draft of this function added an
 * *extra* bonus table on top of ceil() for small remainders, which actually
 * double-counted and produced one more table than needed (e.g. 145 guests
 * incorrectly became 20 tables instead of 19). Plain ceil() is correct.
 */
export function calculateRoundTableCount(seatableGuests: number): number {
  if (seatableGuests <= 0) return 0;
  return Math.ceil(seatableGuests / SEATS_PER_ROUND_TABLE);
}

/** Rectangle (8') table count for Long Table Feast style. */
export function calculateRectTable8Count(seatableGuests: number): number {
  if (seatableGuests <= 0) return 0;
  return Math.ceil(seatableGuests / SEATS_PER_RECT_8_TABLE);
}

/**
 * Full seating plan for a given guest count, head table config, and style.
 * This is the single function the generation engine calls to get all the
 * numbers it needs before placing anything.
 */
export function buildSeatingPlan(
  guestCount: number,
  headTableType: HeadTableType,
  headTablePartySize: number | null,
  layoutStyle: LayoutStyle
): SeatingPlan {
  const seatableGuests = calculateSeatableGuests(guestCount, headTableType, headTablePartySize);

  if (layoutStyle === 'long_table_feast') {
    return {
      seatableGuests,
      roundTableCount: 0,
      rectTable8Count: calculateRectTable8Count(seatableGuests),
      cocktailTableCount: 0,
      loungeSeatedGuests: 0,
      loungeStandingGuests: 0,
    };
  }

  if (layoutStyle === 'lounge_cocktail') {
    const loungeSeatedGuests = Math.round(seatableGuests * LOUNGE_SEATED_RATIO);
    const loungeStandingGuests = seatableGuests - loungeSeatedGuests;
    const cocktailTableCount = Math.ceil(loungeStandingGuests / STANDING_GUESTS_PER_COCKTAIL_TABLE);
    return {
      seatableGuests,
      roundTableCount: calculateRoundTableCount(loungeSeatedGuests),
      rectTable8Count: 0,
      cocktailTableCount,
      loungeSeatedGuests,
      loungeStandingGuests,
    };
  }

  // classic_banquet and mixed_modern: round tables only for guest seating.
  return {
    seatableGuests,
    roundTableCount: calculateRoundTableCount(seatableGuests),
    rectTable8Count: 0,
    cocktailTableCount: 0,
    loungeSeatedGuests: 0,
    loungeStandingGuests: 0,
  };
}
